export enum IntegrationType {
  // Organization-level integrations
  GITHUB = 'GITHUB',
  QUICKBOOKS = 'QUICKBOOKS',
  FRESHBOOKS = 'FRESHBOOKS',

  // User-level integrations
  GOOGLE_CALENDAR = 'GOOGLE_CALENDAR',
  OUTLOOK_CALENDAR = 'OUTLOOK_CALENDAR',
  ZOOM = 'ZOOM',

  // Email integrations (user-level)
  GMAIL = 'GMAIL',
  OUTLOOK_EMAIL = 'OUTLOOK_EMAIL',

  // AI/LLM integrations
  MCP = 'MCP', // Model Context Protocol
}

export enum IntegrationScope {
  ORGANIZATION = 'ORGANIZATION',
  USER = 'USER',
}

export enum IntegrationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ERROR = 'ERROR',
  PENDING_AUTH = 'PENDING_AUTH',
  EXPIRED = 'EXPIRED',
  SUSPENDED = 'SUSPENDED',
}

export enum SyncStatus {
  IDLE = 'IDLE',
  SYNCING = 'SYNCING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
}

export enum SyncDirection {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
  BIDIRECTIONAL = 'BIDIRECTIONAL',
}

export enum SyncEntityType {
  // Organization-level entities
  CUSTOMERS = 'CUSTOMERS',
  PROJECTS = 'PROJECTS',
  TIME_ENTRIES = 'TIME_ENTRIES',
  INVOICES = 'INVOICES',
  ITEMS = 'ITEMS',
  ACCOUNTS = 'ACCOUNTS',
  EMPLOYEES = 'EMPLOYEES',
  ESTIMATES = 'ESTIMATES',
  ENTITIES = 'ENTITIES',
  REPOSITORIES = 'REPOSITORIES',
  PULL_REQUESTS = 'PULL_REQUESTS',

  // User-level entities
  CALENDAR_EVENTS = 'CALENDAR_EVENTS',
  TASKS = 'TASKS',
  NOTES = 'NOTES',
  TODOS = 'TODOS',
  EMAILS = 'EMAILS',
}

// Email sync frequency options
export enum EmailSyncFrequency {
  EVERY_5_MINUTES = 'EVERY_5_MINUTES',
  EVERY_15_MINUTES = 'EVERY_15_MINUTES',
  EVERY_30_MINUTES = 'EVERY_30_MINUTES',
  EVERY_HOUR = 'EVERY_HOUR',
  EVERY_4_HOURS = 'EVERY_4_HOURS',
  EVERY_12_HOURS = 'EVERY_12_HOURS',
  DAILY = 'DAILY',
  MANUAL = 'MANUAL',
}

// How to match emails to contacts
export enum EmailMatchMode {
  DOMAIN_ONLY = 'DOMAIN_ONLY', // Match by email domain against contact company/website
  EMAIL_ONLY = 'EMAIL_ONLY', // Match by exact email address only
  DOMAIN_AND_EMAIL = 'DOMAIN_AND_EMAIL', // Match by both (most comprehensive)
}

// Email sync configuration settings
export interface EmailSyncSettings {
  syncFrequency: EmailSyncFrequency
  lookbackDays: number
  matchMode: EmailMatchMode
  excludeDomains: string[]
  syncInbound: boolean
  syncOutbound: boolean
}

// Email sync configuration
export interface EmailSyncConfiguration {
  id: string
  connectionId: string
  syncFrequency: EmailSyncFrequency
  lookbackDays: number
  matchMode: EmailMatchMode
  excludeDomains: string[]
  syncInbound: boolean
  syncOutbound: boolean
  lastFullSyncAt?: Date
  lastIncrementalSyncAt?: Date
  syncCursor?: string
  createdAt: Date
  updatedAt: Date
}

// Gmail OAuth credentials
export interface GmailCredentials {
  accessToken: string
  refreshToken: string
  tokenExpiresAt: Date
  email: string
  name?: string
}

// Outlook/Microsoft credentials
export interface OutlookCredentials {
  accessToken: string
  refreshToken: string
  tokenExpiresAt: Date
  email: string
  name?: string
}

export interface IntegrationConnection {
  id: string
  organizationId: string
  userId?: string
  type: IntegrationType
  scope: IntegrationScope
  name: string
  status: IntegrationStatus
  credentials: Record<string, any>
  settings: Record<string, any>
  metadata: Record<string, any>
  lastSyncAt?: Date
  lastHealthCheck?: Date
  healthStatus?: string
  errorMessage?: string
  errorCount: number
  createdAt: Date
  updatedAt: Date
  createdBy: string
}

export interface IntegrationSyncConfiguration {
  id: string
  connectionId: string
  entityType: SyncEntityType
  direction: SyncDirection
  isEnabled: boolean
  syncInterval?: number
  lastSyncAt?: Date
  syncStatus: SyncStatus
  settings: Record<string, any>
  fieldMappings: Record<string, any>
  filters: Record<string, any>
  createdAt: Date
  updatedAt: Date
}

export interface IntegrationSyncLog {
  id: string
  connectionId: string
  configurationId?: string
  entityType: SyncEntityType
  direction: SyncDirection
  status: SyncStatus
  startedAt: Date
  completedAt?: Date
  recordsTotal: number
  recordsSuccess: number
  recordsError: number
  recordsSkipped: number
  errorMessage?: string
  metadata: Record<string, any>
  createdAt: Date
  triggeredBy?: string
}

export interface IntegrationMapping {
  id: string
  connectionId: string
  entityType: string
  internalId: string
  externalId: string
  externalData: Record<string, any>
  lastSyncAt: Date
  createdAt: Date
  updatedAt: Date
}

/** Generic creation reserves a pending connection; provider flows own authentication. */
export interface CreateIntegrationConnectionDto {
  type: IntegrationType
  name: string
  settings?: Record<string, any>
}

/** Provider credentials, identity and authentication status are not generic edits. */
export interface UpdateIntegrationConnectionDto {
  name?: string
  status?: IntegrationStatus.INACTIVE
  settings?: Record<string, any>
}

export interface QuickBooksCredentials {
  accessToken: string
  refreshToken: string
  realmId: string
  tokenExpiresAt: Date
  refreshTokenExpiresAt: Date
}

export interface QuickBooksCompanyInfo {
  companyName: string
  companyId: string
  country: string
  currency: string
  fiscalYearStart?: string
}

export interface QuickBooksCustomer {
  id: string
  name: string
  companyName?: string
  email?: string
  phone?: string
  website?: string
  billingAddress?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  active: boolean
  balance: number
  createdTime: Date
  lastUpdatedTime: Date
}

export interface QuickBooksItem {
  id: string
  name: string
  description?: string
  type: 'Service' | 'Inventory' | 'NonInventory'
  unitPrice?: number
  incomeAccountRef?: {
    value: string
    name: string
  }
  expenseAccountRef?: {
    value: string
    name: string
  }
  active: boolean
  createdTime: Date
  lastUpdatedTime: Date
}

export interface QuickBooksTimeActivity {
  id: string
  customerRef?: {
    value: string
    name: string
  }
  itemRef?: {
    value: string
    name: string
  }
  employeeRef?: {
    value: string
    name: string
  }
  description?: string
  hours: number
  minutes: number
  hourlyRate?: number
  date: Date
  billableStatus: 'Billable' | 'NotBillable' | 'HasBeenBilled'
  createdTime: Date
  lastUpdatedTime: Date
}

export interface IntegrationProviderConfig {
  type: IntegrationType
  name: string
  displayName: string
  description: string
  logoUrl?: string
  isVisible?: boolean
  authType: 'OAUTH2' | 'API_KEY' | 'BASIC'
  scope: IntegrationScope
  supportedEntities: SyncEntityType[]
  capabilities: {
    canImport: boolean
    canExport: boolean
    supportsBidirectional: boolean
    supportsRealtime: boolean
    supportsWebhooks: boolean
  }
}

export const INTEGRATION_PROVIDERS: Record<
  IntegrationType,
  IntegrationProviderConfig
> = {
  [IntegrationType.GITHUB]: {
    type: IntegrationType.GITHUB,
    name: 'github',
    displayName: 'GitHub',
    description:
      'Connect repositories to projects, create branches and pull requests from tasks, and sync PR status back to Scrambl',
    logoUrl: '/integrations/github.png',
    authType: 'OAUTH2',
    scope: IntegrationScope.ORGANIZATION,
    supportedEntities: [
      SyncEntityType.REPOSITORIES,
      SyncEntityType.PROJECTS,
      SyncEntityType.TASKS,
      SyncEntityType.PULL_REQUESTS,
    ],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: true,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.QUICKBOOKS]: {
    type: IntegrationType.QUICKBOOKS,
    name: 'quickbooks',
    displayName: 'QuickBooks Online',
    description:
      'Sync customers, invoices, accounts, and time entries with QuickBooks Online for your entire organization',
    logoUrl: '/integrations/quickbooks.png',
    isVisible: false,
    authType: 'OAUTH2',
    scope: IntegrationScope.ORGANIZATION,
    supportedEntities: [
      SyncEntityType.CUSTOMERS,
      SyncEntityType.TIME_ENTRIES,
      SyncEntityType.INVOICES,
      SyncEntityType.ITEMS,
      SyncEntityType.ACCOUNTS,
      SyncEntityType.EMPLOYEES,
    ],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: false,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.FRESHBOOKS]: {
    type: IntegrationType.FRESHBOOKS,
    name: 'freshbooks',
    displayName: 'FreshBooks',
    description: 'Sync time tracking and invoicing data with FreshBooks',
    logoUrl: '/integrations/freshbooks.png',
    isVisible: false,
    authType: 'OAUTH2',
    scope: IntegrationScope.ORGANIZATION,
    supportedEntities: [
      SyncEntityType.CUSTOMERS,
      SyncEntityType.PROJECTS,
      SyncEntityType.TIME_ENTRIES,
      SyncEntityType.INVOICES,
    ],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: false,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.GOOGLE_CALENDAR]: {
    type: IntegrationType.GOOGLE_CALENDAR,
    name: 'google-calendar',
    displayName: 'Google Calendar',
    description:
      'Connect your personal Google Calendar to sync tasks and time entries from your Scrambl projects',
    logoUrl: '/integrations/google_calendar.png',
    authType: 'OAUTH2',
    scope: IntegrationScope.USER,
    supportedEntities: [SyncEntityType.CALENDAR_EVENTS, SyncEntityType.TASKS],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: true,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.OUTLOOK_CALENDAR]: {
    type: IntegrationType.OUTLOOK_CALENDAR,
    name: 'outlook-calendar',
    displayName: 'Outlook Calendar',
    description: 'Sync tasks and time entries with Outlook Calendar',
    logoUrl: '/integrations/ms365_calendar.png',
    authType: 'OAUTH2',
    scope: IntegrationScope.USER,
    supportedEntities: [SyncEntityType.CALENDAR_EVENTS, SyncEntityType.TASKS],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: true,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.ZOOM]: {
    type: IntegrationType.ZOOM,
    name: 'zoom',
    displayName: 'Zoom',
    description: 'Create Zoom meetings for your Scrambl booking links',
    authType: 'OAUTH2',
    scope: IntegrationScope.USER,
    supportedEntities: [SyncEntityType.CALENDAR_EVENTS],
    capabilities: {
      canImport: false,
      canExport: true,
      supportsBidirectional: false,
      supportsRealtime: false,
      supportsWebhooks: false,
    },
  },
  [IntegrationType.GMAIL]: {
    type: IntegrationType.GMAIL,
    name: 'gmail',
    displayName: 'Gmail',
    description:
      'Sync emails with your contacts automatically. Connect your Gmail account to track all email communications.',
    logoUrl: '/integrations/gmail.png',
    authType: 'OAUTH2',
    scope: IntegrationScope.USER,
    supportedEntities: [SyncEntityType.EMAILS],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: false,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.OUTLOOK_EMAIL]: {
    type: IntegrationType.OUTLOOK_EMAIL,
    name: 'outlook-email',
    displayName: 'Microsoft Outlook',
    description:
      'Sync emails with your contacts automatically. Connect your Outlook/Microsoft 365 account to track all email communications.',
    logoUrl: '/integrations/outlook.png',
    authType: 'OAUTH2',
    scope: IntegrationScope.USER,
    supportedEntities: [SyncEntityType.EMAILS],
    capabilities: {
      canImport: true,
      canExport: true,
      supportsBidirectional: true,
      supportsRealtime: false,
      supportsWebhooks: true,
    },
  },
  [IntegrationType.MCP]: {
    type: IntegrationType.MCP,
    name: 'mcp',
    displayName: 'Model Context Protocol',
    description:
      'Enable AI assistants like Claude and GPT to interact with your Scrambl workspace through a secure API',
    logoUrl: '/integrations/mcp.png',
    authType: 'API_KEY',
    scope: IntegrationScope.USER,
    supportedEntities: [
      SyncEntityType.TASKS,
      SyncEntityType.PROJECTS,
      SyncEntityType.ENTITIES,
      SyncEntityType.TIME_ENTRIES,
    ],
    capabilities: {
      canImport: false,
      canExport: false,
      supportsBidirectional: true,
      supportsRealtime: true,
      supportsWebhooks: false,
    },
  },
}

export function isIntegrationVisible(type: IntegrationType): boolean {
  return INTEGRATION_PROVIDERS[type]?.isVisible !== false
}

export function getVisibleIntegrationProviders(
  scope?: IntegrationScope,
): IntegrationProviderConfig[] {
  return Object.values(INTEGRATION_PROVIDERS).filter(
    provider =>
      provider.isVisible !== false && (!scope || provider.scope === scope),
  )
}
